/* eslint-disable jsx-a11y/label-has-for */
/* eslint-disable jsx-a11y/label-has-associated-control */
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { darkModeAware, withStyles } from '@glu/theming';
import { withInputAPI } from '@glu/validation-react';
import Error from '../Error/Error';
import LengthIndicator from '../LengthIndicator/LengthIndicator';
import LabelText from '../LabelText/LabelText';
import OptionalInlineText from '../OptionalInlineText/OptionalInlineText';
import InputComponent from './InputComponent';
import styles from './Input.styles';

let uniqueId = 0;

const propTypes = {
  /** Name attribute */
  name: PropTypes.string.isRequired,
  /** Text for the span inside the label */
  labelText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  /** Classes provided by the theme */
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  /** Custom classes applied to label wrapping component */
  className: PropTypes.string,
  /** Custom classes applied to label span element */
  labelSpanClass: PropTypes.string,
  /** Boolean controlling whether the labelText should only be shown to screen readers */
  screenReaderLabel: PropTypes.bool,
  /** Custom classes applied to input element */
  inputClass: PropTypes.string,
  /** Id for the input if a randomly generated id isn't desired */
  htmlId: PropTypes.string,
  /** Boolean to indicate whether or not you want the standard inline layout */
  inline: PropTypes.bool,
  /** Boolean to indicate whether to render a text area instead */
  textarea: PropTypes.bool,
  /** The error to display */
  error: PropTypes.string,
  /** Use warning styles if true */
  warning: PropTypes.bool,
  /** Full errors onject. */
  errors: PropTypes.arrayOf(PropTypes.string),
  /** Theme for styling */
  theme: PropTypes.objectOf(PropTypes.any),
  /** dark mode boolean */
  dark: PropTypes.bool,
  /** Boolean to indicate whether the input should receive focus */
  autoFocus: PropTypes.bool,
  /** Shows optional text on label */
  optional: PropTypes.bool,
  /** maxLengthIndicatorLimit is used to display value length counter
   *  shows maximal value length limit */
  maxLengthIndicatorLimit: PropTypes.number,
  /** The html value attribute for the input containing the user entered data */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

const defaultProps = {
  className: '',
  labelSpanClass: '',
  screenReaderLabel: false,
  inputClass: '',
  inline: false,
  textarea: false,
  error: '',
  warning: false,
  errors: undefined,
  htmlId: null,
  theme: {},
  dark: false,
  autoFocus: false,
  optional: false,
  maxLengthIndicatorLimit: undefined,
  value: ''
};

const Input = ({
  labelText,
  classes,
  className,
  labelSpanClass,
  screenReaderLabel,
  inputClass,
  error,
  errors,
  htmlId,
  textarea,
  dark,
  name,
  optional,
  maxLengthIndicatorLimit,
  inline,
  warning,
  /* These shouldn't be passed in as a prop to the input. underscore indicates not used */
  theme: _theme,
  ...props
}) => {
  // eslint-disable-next-line no-plusplus
  const id = useMemo(() => htmlId || `input-${uniqueId++}`, [htmlId]);
  const errorMessage = error || errors?.[0];

  return (
    <label htmlFor={id} className={`${className} ${classes.root}`}>
      <LabelText
        className={labelSpanClass}
        screenReaderLabel={screenReaderLabel}
        label={labelText}
        hasError={!!errorMessage}
        dark={dark}
        optional={optional}
        name={name}
        inline={inline}
        textarea={textarea}
        warning={warning}
      />
      <div className={classes.wrap}>
        <InputComponent
          id={id}
          {...props}
          inputClass={`${inputClass}`}
          textarea={textarea}
          error={errorMessage}
          warning={warning}
          dark={dark}
          name={name}
        />
        <div className={classes.helpWrapper}>
          <Error name={name} error={errorMessage} warning={warning} dark={dark} />
          <LengthIndicator
            value={props.value}
            hasError={!!errorMessage}
            dark={dark}
            maxLengthIndicatorLimit={maxLengthIndicatorLimit}
          />
        </div>
      </div>
      {optional && inline
        ? <OptionalInlineText hasError={!!errorMessage} warning={warning} withTextarea={textarea} />
        : null}
    </label>
  );
};

Input.propTypes = propTypes;
Input.defaultProps = defaultProps;

const StyledInput = darkModeAware(withStyles(styles)(Input));
const InputWithInputAPI = withInputAPI(StyledInput);
InputWithInputAPI.displayName = 'Input';

export default InputWithInputAPI;
export const ValidatedInput = InputWithInputAPI;
export const BaseInput = Input;
